/**
 * Vuetify 2 a11y mixin — adds aria-label to any v-dialog that mounts without one.
 *
 * Background (doc/A11Y.md): Vuetify 2's <v-dialog> renders a `<div role="dialog"
 * aria-modal="true">` wrapper but does NOT forward aria-label or aria-labelledby
 * props onto that node. axe-core's `aria-dialog-name` rule (WCAG 2.1 4.1.2) fires
 * "serious" for every dialog Vuetify renders.
 *
 * This mixin patches the rule at runtime: when a dialog node enters the DOM,
 * we read the text of its first v-toolbar / v-card-title as the accessible name
 * and set it on the wrapper. MutationObserver catches dialogs opened later (e.g.
 * confirmation modals, the staging-password gate).
 *
 * Apply once in App.vue's mixins array — the observer is global to document.body.
 */
export default {
  mounted() {
    if (typeof window === "undefined") return;

    const patchDialog = (node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.matches?.(".v-dialog__content[role='dialog']")) {
        if (
          node.getAttribute("aria-label") ||
          node.getAttribute("aria-labelledby")
        ) {
          return;
        }
        const titleEl = node.querySelector(
          ".v-toolbar__title, .v-toolbar, .v-card__title, [aria-label]"
        );
        const labelText =
          titleEl?.getAttribute?.("aria-label") || titleEl?.textContent?.trim();
        if (labelText) node.setAttribute("aria-label", labelText);
      }
      node
        .querySelectorAll?.(".v-dialog__content[role='dialog']")
        .forEach(patchDialog);
    };

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) m.addedNodes.forEach(patchDialog);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document
      .querySelectorAll(".v-dialog__content[role='dialog']")
      .forEach(patchDialog);

    this._vuetifyDialogA11yObserver = observer;
  },
  beforeDestroy() {
    this._vuetifyDialogA11yObserver?.disconnect?.();
  },
};
